import React from 'react';
import { AudioLines, PhoneCall } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import type { Database } from '../types/database';
import { getMediaUrl } from '../lib/utils';
import { Button } from './ui/button';
import { VOICE_OPTIONS } from '../lib/constants';
import { isFromAppStores } from '@/lib/detection';

interface CharacterSummaryProps {
  character: Database['eh']['Tables']['characters']['Row'] | null;
  isLoading: boolean;
  customImage?: string;
  isInDialog?: boolean;
  onEditVoice?: () => void;
  onStartCall?: () => void;
  // isGenerating?: boolean;
  // customPrompt?: string;
}

const CALLS_ENABLED = true;

export const CharacterSummaryContent: React.FC<CharacterSummaryProps> = ({ 
  character, 
  isLoading, 
  customImage,
  isInDialog = false,
  onEditVoice,
  onStartCall,
  // isGenerating = false,
  // customPrompt = ''
}) => {
  // Get the best image for header
  const getHeaderImage = () => {
    if (character?.profile_media_fpaths && character.profile_media_fpaths.length > 0) {
      return getMediaUrl(character.profile_media_fpaths[0]);
    }
    if (character?.avatar_fpath) {
      return getMediaUrl(character.avatar_fpath);
    }
    if (customImage) return customImage;
    return '';
  };
  
  const headerImage = getHeaderImage();
  const voiceLabel = VOICE_OPTIONS.find(v => v.value === character?.voice)?.label;
  
  return (
    <div className="w-full h-full overflow-y-auto bg-zinc-900">
      {/* Header Image (Desktop only) */}
      <div className="w-full max-h-[608px] aspect-[3/4] relative">
        {(isLoading && !customImage) ? (
          <div className="w-full h-full bg-zinc-800 animate-pulse"></div>
        ) : (
          headerImage && (
            <img
              src={headerImage} 
              alt={character?.first_name || 'Character'}
              // object-[center_30%] 30% top to hide AIHP watermark
              className="w-full h-full object-cover object-[center_30%]"
            />
          )
        )}
      </div>
      
      {/* isGenerating ? (
        <div className="w-full flex flex-col items-center p-6 sm:p-4 sm:pt-6">
          <div className="w-full max-w-lg space-y-4">
            <h2 className="text-2xl font-bold text-white mb-2 text-center">Creating Character...</h2>
            <p className="text-gray-300 text-center mb-4">
              We're generating a unique character based on the provided image{customPrompt ? ` and your prompt: "${customPrompt}"` : ''}.
            </p>
            <div className="flex justify-center">
              <div className="w-12 h-12 border-4 border-zinc-600 border-t-pink-500 rounded-full animate-spin"></div>
            </div>
          </div>
        </div>
      ) : */(character && !isLoading) ? (
        <div className="w-full flex flex-col items-center p-3 xl:p-4">
          {/* Name */}
          {isInDialog ? (
            <DialogTitle className="md:hidden xl:block text-xl xl:text-2xl font-bold text-white mb-2 text-center">
              {character.first_name} {character.last_name}
            </DialogTitle>
          ) : (
            <h2 className="md:hidden xl:block text-xl xl:text-2xl font-bold text-white mb-2 text-center">
              {character.first_name} {character.last_name}
            </h2>
          )}
          
          <div className="w-full max-w-lg mb-4 flex flex-col gap-2 justify-center items-center">
            {CALLS_ENABLED && onStartCall && (
              <Button
                variant="pink"
                size="lg"
                className="w-full"
                onClick={onStartCall}
              >
                <PhoneCall />
                <span>Call Me</span>
              </Button>
            )}
            {onEditVoice && (
              <Button
                variant="ghost"
                onClick={onEditVoice}
              >
                <span>{voiceLabel ? `Voice: ${voiceLabel}` : 'Choose Voice'}</span>
                <AudioLines className="size-4" />
              </Button>
            )}
          </div>

          {/* About */}
          {isInDialog ? (
            <DialogDescription className="text-sm text-gray-300 text-left mb-4 whitespace-pre-line">{character.aboutme}</DialogDescription>
          ) : (
            <div className="text-sm text-gray-300 text-left mb-4 whitespace-pre-line">{character.aboutme}</div>
          )}
          {/* Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-lg">
            <SummaryField label="Age" value={character.age} />
            <SummaryField 
              label="Ethnicity" 
              value={character.ethnicity} 
              className="col-span-2 xl:col-span-1"
            />
            <SummaryField
              label="Personality"
              value={character.personality}
              className="col-span-2 xl:col-span-1"
            />
            <SummaryField
              label="Relationship"
              value={character.relationship}
              className="col-span-2 xl:col-span-1"
            />
            <SummaryField
              label="Occupation"
              value={character.occupation}
              className="col-span-2"
            />
            <SummaryField
              label="Hobbies"
              value={Array.isArray(character.hobbies) ? character.hobbies.join(', ') : character.hobbies}
              className="col-span-2"
            />
            {!isFromAppStores() && (
              <SummaryField
                label="Kinks"
                value={Array.isArray(character.kinks) ? character.kinks.join(', ') : character.kinks}
                className="col-span-2"
              />
            )}
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-col items-center p-6 sm:p-4 sm:pt-6">
          <div className="w-1/2 h-12 bg-zinc-800 rounded-lg animate-pulse mb-4"></div>
          <div className="w-full h-24 bg-zinc-800 rounded-lg animate-pulse"></div>
        </div>
      )}
    </div>
  );
};

interface CharacterSummaryDialogProps extends CharacterSummaryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CharacterSummaryDialog: React.FC<CharacterSummaryDialogProps> = ({ 
  open, 
  onOpenChange, 
  character,
  isLoading,
  customImage,
  onEditVoice,
  onStartCall,
  // isGenerating,
  // customPrompt
}) => {
  if (!character && !customImage) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 sm:max-w-4xl w-full h-full md:h-min md:max-w-xl bg-zinc-900 border-none text-white overflow-hidden">
        <CharacterSummaryContent 
          character={character} 
          isLoading={isLoading} 
          customImage={customImage}
          isInDialog={true}
          onEditVoice={onEditVoice}
          onStartCall={onStartCall}
          // isGenerating={isGenerating}
          // customPrompt={customPrompt}
        />
      </DialogContent>
    </Dialog>
  );
};

interface SummaryFieldProps {
  className?: string;
  label: string;
  value: React.ReactNode;
}

const SummaryField: React.FC<SummaryFieldProps> = ({ className, label, value }) => (
  <div className={`flex flex-col ${className}`}>
    <span className="text-xs text-zinc-400 font-semibold mb-1">{label}</span>
    <span className="text-sm text-white break-words capitalize">{value || <span className="text-zinc-500">—</span>}</span>
  </div>
); 